// 🌐 React & State
import React, {
  ElementType,
  FormEvent,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  RefObject,
  useEffect,
  useState,
  useRef,
} from "react";
import { observer } from "mobx-react-lite";
import { useDrop } from "react-dnd";

// 🧠 State Management
import pageBuilderStore from "../../../stores/pageBuilderStore";
import glideBarManager from "../../../stores/glidebar-manager";
import { debugPageContent } from "../../../utils/debug";

// 🧩 Types
import {
  IDimensionField,
  TextTransformType,
} from "../../../stores/state-interfaces";
import { IDesignBlock, IContentBlock } from "../../../types/iblock";
import {
  IButtonBlockData,
  IHeroSlide,
  IHeroSliderBlockData,
  IColumnsBlockData,
  IDividerBlockData,
  IHeadingBlockData,
  IHtmlBlockData,
  IImageBlockData,
  ISocialsBlockData,
  ITextBlockData,
  IVideoBlockData,
} from "../../../types/content-block-data";

import ColumnCell from "./column-cell";

// 🎨 Icons (MUI)
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import TwitterIcon from "@mui/icons-material/Twitter";
import YouTubeIcon from "@mui/icons-material/YouTube";
import blocksManager from "../../../stores/blocks-manager";
import contentManager from "../../../stores/content-manager";
import { ChevronLeft, ChevronRight, PlayCircle } from "lucide-react";
import {
  SiFacebook,
  SiX,
  SiInstagram,
  SiLinkedin,
  SiYoutube,
  SiTiktok,
  SiWhatsapp,
  SiTelegram,
  SiGithub,
} from "react-icons/si";

// Serialization
import serializationManager from "../../../stores/serialization-manager";
import bodyStylesManager from "../../../stores/body-styles-manager";

import contentHistoryManager from '../../../stores/content-history-manager';
import { reaction, toJS } from "mobx";

function mergeValueUnit(
  a?: { value?: string; unit?: string },
  b?: { value?: string; unit?: string },
  defVal = "0",
  defUnit = "px"
): { value: string; unit: string } {
  return {
    value: b?.value ?? a?.value ?? defVal,
    unit: b?.unit ?? a?.unit ?? defUnit,
  };
}

/**
 * Props for {@link ColumnsBlock} (UI Component).
 *
 * @remarks
 * Extends the data-only interface {@link IColumnsBlockData}, adding React/UI props
 * such as `renderBlock`, and `className` for use in rendering and editing in the UI.
 * Data-related fields (like `children` and `columnWidths`) should be defined in
 * {@link IColumnsBlockData}.
 *
 * @see {@link IColumnsBlockData}
 *
 * @property renderBlock Render function to output each IContentBlock (from our block renderer).
 * 										UI-only.
 * @property className		Optional custom CSS classes for styling the columns container. UI-only.
 */
export interface IColumnsBlockProps extends IColumnsBlockData {

  /** References the ColumnsBlock's contentBlockId */
  colContentBlockId: string;

  contentBlockRef?: RefObject<HTMLDivElement>;

  /** Render function to output each IContentBlock (from your block renderer). */
  renderBlock: (block: IDesignBlock) => ReactNode;

  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;

  /** Optional: Custom className for styling. */
  className?: string;
}

/**
 * `ColumnsBlock` – Renders a multi-column content block layout.
 *
 * @component
 * @description
 * This block renders a grid of droppable columns, where each column contains
 * an independent list of content blocks. It supports dynamic column widths
 * and serves as the container for individual `ColumnCell` components.
 *
 * @remarks
 * - `children` should be passed as a 2D array: each inner array = one column's content blocks
 * - `columnWidths` should match the number of columns and contain `fr` ratios (e.g., [0.5, 0.5])
 * - Drag-and-drop behavior is delegated down to each `ColumnCell`
 * - Added the function `moveBlock(fromColIndex, srcBlock, toColIndex, toBlock)` to act as a contract
 * for moving one DesignBlock from one column to the next.
 *
 * @props
 * @param {IDesignBlock[][]} children – List of columns (each column is a list of blocks)
 * @param {number[]} [columnWidths] – Optional width ratios for each column
 * @param {string} [className] – Additional classes for styling the wrapper
 * @param {(block: IDesignBlock) => ReactNode} renderBlock – Function to render individual content blocks
 *
 * @since 2025-06-15
 * @lastUpdated 2025-06-21
 *
 * @future
 * - Add sidebar-based controls for adjusting column count and width
 */
export function ColumnsBlock({
  colContentBlockId,
  contentBlockRef,
  children,
  columnWidths,
  columnGap,
  rowGap,
  cellPadding,
  cellMargin,
  cellRadius,
  onClick,
  className = "",
}: IColumnsBlockProps) {
  const columnCount = children.length;
  // Default: equal width for each column if not provided
  const widths =
    columnWidths && columnWidths.length === columnCount
      ? columnWidths
      : Array(columnCount).fill(1 / columnCount);

  // Map to CSS properties
  // Always fallback to sensible defaults!
  const containerStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: widths.map((w) => `${w}fr`).join(" "),
    columnGap: columnGap?.value
      ? `${columnGap.value}${columnGap.unit || "px"}`
      : "8px", // default 8px
    rowGap: rowGap?.value ? `${rowGap.value}${rowGap.unit || "px"}` : "0px", // default 0px
  };

  const cellStyle: React.CSSProperties = {
    padding: blocksManager.dimensionToCss(cellPadding),
    margin: blocksManager.dimensionToCss(cellMargin),
    borderRadius: blocksManager.dimensionToCss(cellRadius),
    overflow: parseInt(cellRadius?.topLeft!) > 0 
      || parseInt(cellRadius?.topRight!) > 0
      || parseInt(cellRadius?.bottomLeft!) > 0
      || parseInt(cellRadius?.bottomRight!) > 0  ? "hidden" : "auto"
  };

  console.log(cellPadding);

  return (
    <div
      ref={contentBlockRef}
      className={`grid border border-dashed border-gray-300 p-2 ${className}`}
      style={containerStyle}
      onClick={onClick}
    >
      {children.map((blocks, colIdx) => (
        <ColumnCell
          key={colIdx}
          colContentBlockId={colContentBlockId}
          colIndex={colIdx}
          children={blocks}
          widthScale={widths[colIdx] ?? 1 / children.length}
          style={cellStyle} // Pass cell styles here!
        />
      ))}
    </div>
  );
}

/**
 * Props for {@link ButtonBlock} (UI Component).
 *
 * @remarks
 * - Extends {@link IButtonBlockData}, which contains the data fields (e.g. `label`).
 * - These props are for React UI rendering/editing and should not be serialized.
 *
 * @see {@link IButtonBlockData}
 *
 * @property isEditing	        If true, disables button interactions.
 * @property contentBlockRef		Optional ref to the button element (for toolbars, focus, etc).
 * @property onClick		        Optional click handler for the button.
 * @property className	        Optional custom CSS classes for styling.
 */
export interface IButtonBlockProps extends IButtonBlockData {
  isEditing?: boolean;
  contentBlockRef?: RefObject<HTMLButtonElement>;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  className?: string;
}

/**
 * ButtonBlock – Renders a customizable button block.
 *
 * @remarks
 * * This button appears plain by default; no color or style is set.
 *  * All visual customization (color, size, etc.) should be applied via the properties panel
 *  * and passed as className. This makes it easy to support color pickers or style controls later.
 *
 * @example
 * <ButtonBlock
 *   label="Submit"
 *   onClick={handleSubmit}
 *   isEditing={false}
 *   contentBlockRef={ref}
 *   className="bg-green-600 text-white"
 * />
 */
export function ButtonBlock({
  actionType,
  url,
  target,
  color: textColor,
  backgroundColor,
  hoverColor: hoverTextColor,
  hoverBackgroundColor,
  label,
  fontFamily,
  fontSize,
  fontWeight,
  lineHeight,
  letterSpacing,
  textDecoration,
  padding,
  margin,
  borderRadius,
  borderStyle,
  borderWidth,
  borderColor,
  width,
  alignment,
  isEditing = false,
  contentBlockRef,
  onClick,
  className = "",
}: IButtonBlockProps) {
  const [isHover, setIsHover] = useState(false);

  const style: React.CSSProperties = {
    color: isHover
      ? hoverTextColor || textColor || "#fff"
      : textColor || "#fff",
    fontFamily: fontFamily || "Inter, sans-serif",
    fontSize: fontSize?.value
      ? `${fontSize.value}${fontSize.unit || "rem"}`
      : "1rem",
    fontWeight: fontWeight || "500",
    lineHeight: lineHeight?.value
      ? `${lineHeight.value}${lineHeight.unit || ""}`
      : undefined,
    letterSpacing: letterSpacing?.value
      ? `${letterSpacing.value}${letterSpacing.unit || "px"}`
      : undefined,
    textDecoration: textDecoration || "none",
    backgroundColor: isHover
      ? hoverBackgroundColor || backgroundColor || "#2563eb"
      : backgroundColor || "#2563eb",
    borderStyle: borderStyle || "none",
    borderWidth: borderWidth?.value
      ? `${borderWidth.value}${borderWidth.unit || "px"}`
      : "0",
    borderColor: borderColor || "transparent",
    padding: blocksManager.dimensionToCss(padding),
    margin: blocksManager.dimensionToCss(margin),
    borderRadius: blocksManager.dimensionToCss(borderRadius),
    width: width?.value ? `${width.value}${width.unit || "px"}` : undefined,
    // ...other properties can go here later
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent:
          alignment === "left"
            ? "flex-start"
            : alignment === "right"
              ? "flex-end"
              : "center", // fallback to center
      }}
    >
      <button
        ref={contentBlockRef}
        className={`px-4 py-2 rounded ` + className}
        style={style}
        tabIndex={0}
        disabled={isEditing}
        onClick={onClick}
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
        type="button"
      >
        {label}
      </button>
    </div>
  );
}

/**
 * Props for {@link DividerBlock} (UI Component).
 *
 * @remarks
 * - Extends {@link IDividerBlockData}, which contains all data fields (often empty for divider.)
 * - UI-only props should be used for React rendering/editing only and never serialized.
 * - Maintaining this interface (even if minimal) ensures type consistency when parsing or comparing
 * 	 block types.
 *
 * @see {@link IDividerBlockData}
 *
 * @property contentBlockRef		Optional ref to the divider element (for toolbars, focus, etc).
 * @property className	        Optional custom CSS classes for styling.
 */
export interface IDividerBlockProps extends IDividerBlockData {
  contentBlockRef?: RefObject<HTMLHRElement>;
  className?: string;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
}

/**
 * DividerBlock – Renders a simple horizontal divider (hr) block.
 *
 * @remarks
 * The divider is styled minimally and stretches the full width of its container.
 * Future enhancements can allow adjustment of thickness, color, or style via the properties panel.
 *
 * @example
 * <DividerBlock
 *   contentBlockRef={ref}
 *   className="my-8"
 * />
 */
export function DividerBlock({
  width,
  height,
  color,
  padding,
  margins,
  alignment,
  contentBlockRef,
  onClick,
  className = "",
}: IDividerBlockProps) {
  const style: React.CSSProperties = {
    width: width?.value ? `${width.value}${width.unit || "%"}` : "100%",
    height: height?.value ? `${height.value}${height.unit || "px"}` : "1px",
    backgroundColor: color || "#CCCCCC",
    padding: blocksManager.dimensionToCss(padding),
    margin: blocksManager.dimensionToCss(margins),
    border: "none",
    display: "block",
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent:
          alignment === "left"
            ? "flex-start"
            : alignment === "right"
              ? "flex-end"
              : "center", // default to center
      }}
    >
      <hr ref={contentBlockRef} style={style} className={className} />
    </div>
  );
}

/**
 * Props for {@link HeadingBlock} (UI Component).
 *
 * @remarks
 * - Extends {@link IHeadingBlockData}, which contains all data fields (e.g. `text`, `headingLevel`).
 * - All UI-only props are for React rendering/editing and should never be serialized or saved.
 * - Keeping this separation ensures correct type-checking and safe (de)serialization for heading blocks.
 *
 * @see {@link IHeadingBlockData}
 *
 * @property isEditing        If true, renders the heading as editable (contentEditable).
 * @property contentBlockRef  Optional ref to the heading element (for toolbars, focus, etc).
 * @property onInput          Optional handler called when the heading text changes.
 * @property onClick          Optional handler called when the block is clicked.
 * @property onFocus          Optional handler called when the TextBlock is focused.
 * @property onBlur           Optional handler called when the TextBlock loses focus.
 * @property className        Optional custom CSS classes for styling.
 */
export interface IHeadingBlockProps extends IHeadingBlockData {
  /** If true, renders the heading as editable (contentEditable). */
  isEditing?: boolean;

  /** Ref to the DOM element (for toolbars, focus, etc). */
  contentBlockRef?: RefObject<HTMLHeadingElement>;

  /** Called when the heading block is clicked (to trigger editing/selecting). */
  onClick?: (e: MouseEvent<HTMLDivElement>) => void;

  /** Called when the heading element is focused. */
  onFocus?: (e: React.FocusEvent<HTMLHeadingElement>) => void;

  /** Called when the heading element loses focus. */
  onBlur?: (e: React.FocusEvent<HTMLHeadingElement>) => void;

  /** Optional: Custom className for extra styling. */
  className?: string;
}

/**
 * Inserts a custom field (as an uneditable span) at the current caret or selection within a contentEditable block.
 *
 * - The inserted span is marked with `data-custom-field="true"` and a unique `data-field-value` for easy tracking/removal.
 * - The span is set to `contenteditable="false"` so it cannot be modified by the user.
 * - After insertion, the caret is moved to immediately after the newly inserted field.
 *
 * @param field - The string label or merge tag (e.g., "{first-name}") to insert at the caret position.
 * @param ref - A React ref to the contentEditable element (either a heading or div) where the field should be inserted.
 *
 * @example
 * // Inserts "{first-name}" at the user's current caret in the block
 * insertCustomFieldAtCaret("{first-name}", contentBlockRef);
 */
function insertCustomFieldAtCaret(field: string, ref: RefObject<HTMLDivElement> | RefObject<HTMLHeadingElement>) {
  const el = ref.current;
  if (!el) return;
  el.focus();

  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return;

  const range = sel.getRangeAt(0);
  const span = document.createElement("span");
  span.textContent = field;
  span.setAttribute("data-custom-field", "true");
  span.setAttribute("data-field-value", field); // So you can distinguish each field
  span.setAttribute("contenteditable", "false");
  span.className = "custom-field-badge";
  range.insertNode(span);

  // Move caret after the inserted field
  range.setStartAfter(span);
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
}

function isCaretInsideCustomField() {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return false;
  
  const node = sel.anchorNode;
  if (!node) return false;

  const element =
    node.nodeType === Node.ELEMENT_NODE
      ? (node as HTMLElement)
      : node.parentElement;

  return element?.closest('span[data-custom-field="true"]') != null;
}

function moveCaretOutsideCustomField() {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return;

  const node = sel.anchorNode;
  if (!node) return;

  const span = node.nodeType === Node.ELEMENT_NODE
    ? (node as HTMLElement)
    : node.parentElement;

  const customField = span?.closest('span[data-custom-field="true"]');
  if (customField && customField.parentNode) {
    const range = document.createRange();
    range.setStartAfter(customField);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
  }
}

function selectionIntersectsCustomField(): boolean {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return false;

  for (let i = 0; i < sel.rangeCount; i++) {
    const range = sel.getRangeAt(i);
    // Clone selected DOM and look for custom-field spans
    const frag = range.cloneContents() as DocumentFragment & { querySelector?: any };
    if (frag?.querySelector && frag.querySelector('span[data-custom-field="true"]')) {
      return true;
    }

    // Also guard the "selection exactly inside span" edge cases
    const anchorEl = (sel.anchorNode?.nodeType === Node.ELEMENT_NODE 
      ? (sel.anchorNode as HTMLElement)
      : sel.anchorNode?.parentElement) as HTMLElement | null;
    
    if (anchorEl?.closest?.('span[data-custom-field="true"]')) return true;
  }

  return false;
}

// Get ALL {custom-field} names we support
function getAllCustomFieldTokens(): string[] {
  return pageBuilderStore.getCustomFields(); // e.g., ["{first-name}", "{last-name}", ...]
}

// From a DOM Range, collect any fields inside it
function collectFieldsFromRange(range: Range): string[] {
  const frag = range.cloneContents();
  const spans = frag.querySelectorAll?.('span[data-custom-field="true"]') ?? [];
  const fields = new Set<string>();
  spans.forEach((s: Element) => {
    const v = s.getAttribute("data-field-value");
    if (v) fields.add(v);
  });
  return [...fields];
}

// From clipboard text, find tokens that match supported fields
function collectFieldsFromPlainText(text: string): string[] {
  const known = new Set(getAllCustomFieldTokens());
  const found = new Set<string>();
  // simplest scan: check each token’s presence (tokens are distinct like "{first-name}")
  for (const token of known) {
    if (text.includes(token)) found.add(token);
  }
  return [...found];
}

// Build HTML that converts {tokens} in plain text to <span data-custom-field> badges
function htmlWithCustomFieldSpansFromText(text: string): string {
  // Escape HTML first
  const esc = text
    .replace(/&/g, "&amp;").replace(/</g, "&lt;")
    .replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  // Replace tokens with our span markup
  return getAllCustomFieldTokens().reduce((acc, token) => {
    const span = `<span data-custom-field="true" data-field-value="${token}" contenteditable="false" class="custom-field-badge">${token}</span>`;
    return acc.split(token).join(span);
  }, esc).replace(/\n/g, "<br/>");
}

// types

type SanitizedResult = { html: string, fields: string[] };

// If HTML from clipboard already contains our spans, keep them; strip other styling
function sanitizeIncomingHTMLPreservingCustomFields(html: string): SanitizedResult {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const allowed = new Set(["A","B","STRONG","I","EM","U","S","CODE","BR","P","UL","OL","LI","BLOCKQUOTE","SPAN"]);
  const fields = new Set<string>();

  const walk = (node: Node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      // keep our custom field spans intact
      const isCF = el.tagName === "SPAN" && el.getAttribute("data-custom-field") === "true";
      if (!allowed.has(el.tagName)) {
        const parent = el.parentNode;
        while (el.firstChild) parent?.insertBefore(el.firstChild, el);
        parent?.removeChild(el);
        return;
      }
      if (isCF) {
        const v = el.getAttribute("data-field-value");
        if (v) fields.add(v);
      } else { 
        el.removeAttribute("style"); el.removeAttribute("class"); 
      }
    }
    Array.from(node.childNodes).forEach(walk);
  };
  Array.from(doc.body.childNodes).forEach(walk);
  return { html: doc.body.innerHTML, fields: [...fields] };
}

// plain-text → convert tokens to spans; also return which fields found
export function buildHTMLFromPlainTextWithCF(text: string): SanitizedResult {
  const tokens = pageBuilderStore.getCustomFields();
  // escape
  let esc = text
    .replace(/&/g,"&amp;").replace(/</g,"&lt;")
    .replace(/>/g,"&gt;").replace(/"/g,"&quot;");
  const found = new Set<string>();
  for (const token of tokens) {
    if (esc.includes(token)) {
      found.add(token);
      const span = `<span data-custom-field="true" data-field-value="${token}" contenteditable="false" class="custom-field-badge">${token}</span>`;
      esc = esc.split(token).join(span);
    }
  }
  return { html: esc.replace(/\n/g,"<br/>"), fields: [...found] };
}

/**
 * HeadingBlock – Renders a heading (H1) content block, optionally editable in-place.
 *
 * @remarks
 * Use this block for top-level page headings. Future versions may support heading level (H2, H3, etc.)
 * or additional typography options via props or sidebar configuration.
 *
 * @example
 * <HeadingBlock
 *   text="My Main Title"
 *   isEditing={editing}
 *   contentBlockRef={ref}
 *   onInput={setHeading}
 *   onClick={handleHeadingClick}
 * />
 */
export const HeadingBlock = observer(function HeadingBlock({
  html,
  headingLevel = 1,
  isEditing = true,
  contentBlockRef,
  onClick,
  onFocus,
  onBlur,
  className = "",
  color,
  fontFamily,
  fontSize,
  fontWeight,
  lineHeight,
  letterSpacing,
  textDecoration,
  textTransform,
  alignment,
}: IHeadingBlockProps) {
  const Tag = `h${Math.min(Math.max(headingLevel, 1), 6)}` as keyof ElementType;
  const blockId = blocksManager.getBlockEditing().selectedBlockId;
  const allAddedFields = pageBuilderStore.getAllAddedFields;
  const [locallyUsedFields, setLocallyUsedFields] = useState<{idx: number, field: string}[]>([]);
  const [availableFields, setAvailableFields] = useState<string[]>(pageBuilderStore.getCustomFields().filter(
    field => !allAddedFields.includes(field)
  ));

  useEffect(() => {
    const dispose = reaction(
      () => pageBuilderStore.getAllAddedFields,     // Track observable
      (all) => {
        all.map(f => console.log("Added Field:", f));
        setAvailableFields(pageBuilderStore.getCustomFields().filter(f => !all.includes(f)));
      }
    );
    return () => dispose();
  }, []);

  const style: React.CSSProperties = {
    color: color || "#ffffff",
    fontFamily: fontFamily || "Inter, sans-serif",
    fontSize: fontSize?.value
      ? `${fontSize.value}${fontSize.unit || "rem"}`
      : "1rem",
    fontWeight: fontWeight || "500",
    lineHeight: lineHeight?.value
      ? `${lineHeight.value}${lineHeight.unit || ""}`
      : "1.4rem",
    letterSpacing: letterSpacing?.value
      ? `${letterSpacing.value}${letterSpacing.unit || "px"}`
      : "0px",
    textDecoration: textDecoration || "none",
    textTransform: textTransform,
    textAlign: alignment || "left",
    // ...other properties can go here later
  };

  // Handler for live heading updates
  const handleInput = (e: FormEvent<HTMLHeadingElement>) => {
    // Find all current custom field spans in the content
    const customFieldSpans = e.currentTarget.querySelectorAll('span[data-custom-field="true"]');
    const currentFields = Array.from(customFieldSpans)
      .map(span => span.getAttribute("data-field-value"))
      .filter(Boolean);

    // Now we check what is available in the actual content and match it with our locallyUsedFields
    const removedFields = locallyUsedFields.filter(({ field }) => !currentFields.includes(field));
    if (removedFields.length > 0) {
      // Remove all locallyUsedFields, restore to availableFields
      setLocallyUsedFields(prev => prev.filter(({ field }) => removedFields.every(rf => rf.field !== field)));
      setAvailableFields(prev => {
        let updated = [...prev];
        removedFields.forEach(({idx, field}) => {
          // Insert back at the original index
          updated.splice(idx, 0, field);
        });
        return updated;
      });

      // Update MobX store: remove field from addedFields
      removedFields.forEach(({ field }) => {
        pageBuilderStore.removeAddedField(blockId!, field);
      });

      console.log(JSON.stringify(pageBuilderStore.getAllAddedFields));
    }

    // Update MobX with the *current* fields
    // pageBuilderStore.updateAddedFields(blockId!, currentFields as string[]);

    // Maybe update the current caret position via a state object.
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      // Save the range (clone, as it can change after blur)
      const caretRange = selection.getRangeAt(0).cloneRange();
      // Update our MobX glideBarState here.
      if (caretRange != null) glideBarManager.setCurrCaretPos(caretRange);
    }
  };

  /**
   * Added a custom focus handler because we also need a more efficient way to poll the MobX store 
   * for all the addedFields.
   */
  const handleFocus = (e: React.FocusEvent<HTMLHeadingElement>) => {
    setAvailableFields(pageBuilderStore.getCustomFields().filter(f => !pageBuilderStore.getAllAddedFields.includes(f)));

    availableFields.map(field => console.log("Available Field:", field));

    if (isCaretInsideCustomField()) {
      moveCaretOutsideCustomField();
    }

    if (onFocus) {
      onFocus(e);
    }
  }; 

  /**
   * We need this event handler to prevent the "Copying" functionality.
   */
  const handleCopy: React.ClipboardEventHandler<HTMLElement> = (e) => {
    if (selectionIntersectsCustomField()) {
      e.preventDefault();
      e.stopPropagation();
      pageBuilderStore.setToast("Copying of custom fields is not allowed.");
    }
  };

  /**
   * For detecting when a "cut" action has been performed.
   */
  const handleCut: React.ClipboardEventHandler<HTMLElement> = (e) => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;

    const range = sel.getRangeAt(0);

    // snapshot which fields (and their local indices) were inside the selection
    const removedFields = collectFieldsFromRange(range);     // ["{first-name}", ...]

    if (removedFields.length === 0) return;

    // Allow the default cut to proceed, then sync store/UI
    setTimeout(() => {
      // 1) Update global store
      removedFields.forEach((f) => pageBuilderStore.removeAddedField(blockId!, f));

      // 2) Update this block’s local bars
      setLocallyUsedFields(prev => prev.filter(x => !removedFields.includes(x.field)));
      setAvailableFields(prev => {
        // reinsert back into available at the *front* (simple), or
        // reinsert at original index if you prefer:
        // use the previous locallyUsedFields snapshot to re-place by idx
        const updated = [...prev];
        // simple append (predictable):
        removedFields.forEach(f => { if (!updated.includes(f)) updated.push(f); });
        return updated;
      });
    }, 0);
  };

  /**
   * To ensure that what has been pasted into the HeadingBlock will be stripped of it's styles.
   */
  const handlePasteHTML: React.ClipboardEventHandler<HTMLElement> = (e) => {
    e.preventDefault();
    if (isCaretInsideCustomField()) moveCaretOutsideCustomField();

    const blockId = blocksManager.getBlockEditing().selectedBlockId!;
    const allAdded = pageBuilderStore.getAllAddedFields;
    const availBefore = [...availableFields];             // capture indices
    const idxMap: Record<string, number> = {};
    availBefore.forEach((f, i) => (idxMap[f] = i));

    const html = e.clipboardData.getData("text/html");
    const text = e.clipboardData.getData("text/plain");

    let result: { html: string; fields: string[] };
    if (html && html.includes('data-custom-field="true"')) {
      result = sanitizeIncomingHTMLPreservingCustomFields(html);
    } else {
      result = buildHTMLFromPlainTextWithCF(text ?? "");
    }

    // Insert sanitized HTML
    document.execCommand("insertHTML", false, result.html);

    // Dedupe vs global added list
    const uniqueNew = result.fields.filter(f => !allAdded.includes(f));
    if (uniqueNew.length) {
      pageBuilderStore.updateAddedFields(blockId, [...allAdded, ...uniqueNew]);
      // remove from available + add to locally used with original index (if known)
      setAvailableFields(prev => prev.filter(f => !uniqueNew.includes(f)));
      setLocallyUsedFields(prev => [
        ...prev,
        ...uniqueNew.map(field => ({ idx: idxMap[field] ?? -1, field }))
      ]);
    }

    const sel = window.getSelection();
    if (sel?.rangeCount) glideBarManager.setCurrCaretPos(sel.getRangeAt(0).cloneRange());
  };

  const fontSizeClass = bodyStylesManager.getHeadingFontSizeStyle(headingLevel);
  let blockClassName = "outline-none transition-all w-full";

	const isEmpty = !html || html.trim() === "";

  return (
    <div className={`relative p-2 ${className}`} onClick={onClick}>
      {/* @ts-ignore */}
      <Tag
        ref={contentBlockRef}
        className={`font-bold text-gray-800 min-h-[28px] cursor-text ${fontSizeClass} ${blockClassName} focus:outline-none`}
        contentEditable
        suppressContentEditableWarning={true}
        spellCheck={true}
        tabIndex={0}
        onCopy={handleCopy}
        onCut={handleCut}
        onPaste={handlePasteHTML}
        onMouseUp={() => {
          if (isCaretInsideCustomField()) {
            moveCaretOutsideCustomField();
          }
        }}
        onKeyDown={(e: React.KeyboardEvent) => {
          // For detect what the user presses before the DOM changes
          if ((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'C')) {
            if (selectionIntersectsCustomField()) {
              e.preventDefault();
              e.stopPropagation();
              pageBuilderStore.setToast("Copying of custom fields is not allowed.");
              return;
            }
          }
        }}
        onInput={handleInput}
        onFocus={handleFocus}
        onBlur={onBlur}
        style={style}
        dangerouslySetInnerHTML={{ __html: isEmpty ? `<span className="placeholder">Enter a Heading</span>` : html }}
      ></Tag>
      {/* Custom Fields Bar (render at bottom) */}
      {pageBuilderStore.getIsEmail() && isEditing && availableFields.length > 0 && (
        <div className="w-full p-3 z-20 mt-4 flex gap-2 bg-white bg-opacity-80 rounded-b-xl border-t border-gray-200">
          {availableFields.map(field => (
            <button
              key={field}
              className={"px-3 py-1 bg-gradient-to-b from-[#f8fafc] to-[#e5e7eb] border border-gray-300 text-gray-700 rounded-md cursor-pointer text-xs font-medium hover:border-blue-400 hover:text-blue-500 transition"}
              tabIndex={-1}
              type="button"
              contentEditable={false}
              onMouseDown={e => {
                e.preventDefault();
                // Insert at caret
                if (blockId) {
                  insertCustomFieldAtCaret(field, contentBlockRef!);

                  // Update MobX store with new field
                  pageBuilderStore.updateAddedFields(blockId, [...pageBuilderStore.getAddedFields(blockId), field]);
                  console.log(JSON.stringify(pageBuilderStore.getAllAddedFields));
                  const idx = availableFields.indexOf(field);
                  setLocallyUsedFields(prev => [...prev, {idx, field}]);
                  setAvailableFields(prev => prev.filter(f => f !== field));
                }
              }}
            >
              {field}
            </button>
          ))}
        </div>
      )}
    </div>
  );
});

/**
 * Props for {@link TextBlock} (UI Component).
 *
 * @remarks
 * - Extends {@link ITextBlockData}, which contains the serializable data field (`text`, etc).
 * - All UI-only props below are for use in React rendering/editing only - never persisted or serialized.
 * - This clear separation keeps data models safe and makes testing/maintenance easy.
 *
 * @see {@link ITextBlockData}
 *
 * @property isEditing        If true, renders the block as editable (contentEditable).
 * @property contentBlockRef  Optional ref to the block element (for toolbars, focus, etc).
 * @property onInput          Optional handler called when the text content changes.
 * @property onClick          Optional handler called when the block is clicked.
 * @property onKeyDown        Optional handler called when the user presses a key.
 * @property onFocus          Optional handler called when the TextBlock is focused.
 * @property onBlur           Optional handler called when the TextBlock loses focus.
 * @property className        Optional custom CSS classes for styling.
 */
export interface ITextBlockProps extends ITextBlockData {
  /** If true, renders the block as editable (contentEditable). */
  isEditing?: boolean;

  /** Ref to the DOM element (for toolbars, focus, etc). */
  contentBlockRef?: RefObject<HTMLDivElement>;

  /** Called when the TextBlock has input focuse. */
  onFocus?: (e: React.FocusEvent<HTMLHeadingElement>) => void;

  /** Call when the TextBlock has lost input focus. */
  onBlur?: (e: React.FocusEvent<HTMLHeadingElement>) => void;

  /** Optional: Custom className for extra styling. */
  className?: string;
}

/**
 * TextBlock – Renders a plain text content block, optionally editable in-place.
 *
 * @remarks
 * Use this block for basic paragraph content within the page builder.
 * To allow rich text or more formatting, extend via future props or composition.
 *
 * @example
 * <TextBlock
 *   text="Hello world!"
 *   isEditing={true}
 *   contentBlockRef={ref}
 *   onInput={(val) => setValue(val)}
 *   onClick={handleClick}
 * />
 */
export const TextBlock = observer(function TextBlock({
  html,
  isEditing = true,
  contentBlockRef,
  onFocus,
  onBlur,
  className = "",
  color: textColor,
  fontFamily,
  fontSize,
  fontWeight,
  lineHeight,
  letterSpacing,
  textDecoration,
  textTransform,
  alignment,
}: ITextBlockProps) {
  const blockId = blocksManager.getBlockEditing().selectedBlockId;
  const allAddedFields = pageBuilderStore.getAllAddedFields;
  const [locallyUsedFields, setLocallyUsedFields] = useState<{idx: number, field: string}[]>([]);
  const [availableFields, setAvailableFields] = useState<string[]>([]);

  useEffect(() => {
    const dispose = reaction(
      () => pageBuilderStore.getAllAddedFields,     // Track observable
      (all) => {
        setAvailableFields(pageBuilderStore.getCustomFields().filter(f => !all.includes(f)));
      }
    );
    return () => dispose();
  }, []);

  const style: React.CSSProperties = {
    color: textColor || "#ffffff",
    fontFamily: fontFamily || "Inter, sans-serif",
    fontSize: fontSize?.value
      ? `${fontSize.value}${fontSize.unit || "rem"}`
      : "1rem",
    fontWeight: fontWeight || "500",
    lineHeight: lineHeight?.value
      ? `${lineHeight.value}${lineHeight.unit || ""}`
      : "1.4rem",
    letterSpacing: letterSpacing?.value
      ? `${letterSpacing.value}${letterSpacing.unit || "px"}`
      : "0px",
    textDecoration: textDecoration || "none",
    textTransform: textTransform,
    textAlign: alignment || "left",
    // ...other properties can go here later
  };

  // Handler for live text updates
  const handleInput = (e: FormEvent<HTMLDivElement>) => {
    // Find all current custom field spans in the content
    const customFieldSpans = e.currentTarget.querySelectorAll('span[data-custom-field="true"]');
    const currentFields = Array.from(customFieldSpans)
      .map(span => span.getAttribute("data-field-value"))
      .filter(Boolean);

    // Now we check what is available in the actual content and match it with our locallyUsedFields
    const removedFields = locallyUsedFields.filter(({ field }) => !currentFields.includes(field));
    if (removedFields.length > 0) {
      // Remove all locallyUsedFields, restore to availableFields
      setLocallyUsedFields(prev => prev.filter(({ field }) => removedFields.every(rf => rf.field !== field)));
      setAvailableFields(prev => {
        let updated = [...prev];
        removedFields.forEach(({idx, field}) => {
          // Insert back at the original index
          updated.splice(idx, 0, field);
        });
        return updated;
      });

      // Update MobX store: remove field from addedFields
      removedFields.forEach(({ field }) => {
        pageBuilderStore.removeAddedField(blockId!, field);
      });

      console.log(JSON.stringify(pageBuilderStore.getAllAddedFields));
    }

    // Update MobX with the *current* fields
    // pageBuilderStore.updateAddedFields(blockId!, currentFields as string[]);

    // We should use this to update the current caret position.
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      // Save the range (clone, as it can change after blur)
      const caretRange = selection.getRangeAt(0).cloneRange();

      // Update our MobX glideBarState here.
      if (caretRange != null) glideBarManager.setCurrCaretPos(caretRange);
    }
  };

  /**
   * Added a custom focus handler because we also need a more efficient way to poll the MobX store 
   * for all the addedFields.
   */
  const handleFocus = (e: React.FocusEvent<HTMLDivElement>) => {
    setAvailableFields(pageBuilderStore.getCustomFields().filter(f => !pageBuilderStore.getAllAddedFields.includes(f)));

    availableFields.map(field => console.log("Available Field:", field));
    // currentAll.map(field => console.log("Added field:", field));

    if (isCaretInsideCustomField()) {
      moveCaretOutsideCustomField();
    }

    if (onFocus) {
      onFocus(e);
    }
  }; 

  /**
   * We need this event handler to prevent the "Copying" functionality.
   */
  const handleCopy: React.ClipboardEventHandler<HTMLElement> = (e) => {
    if (selectionIntersectsCustomField()) {
      e.preventDefault();
      e.stopPropagation();
      pageBuilderStore.setToast("Copying of custom fields is not allowed.");
    }
  };

  /**
   * For detecting when a "cut" action has been performed.
   */
  const handleCut: React.ClipboardEventHandler<HTMLElement> = (e) => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;

    const range = sel.getRangeAt(0);

    // snapshot which fields (and their local indices) were inside the selection
    const removedFields = collectFieldsFromRange(range);     // ["{first-name}", ...]

    if (removedFields.length === 0) return;

    // Allow the default cut to proceed, then sync store/UI
    setTimeout(() => {
      // 1) Update global store
      removedFields.forEach((f) => {
        console.log("field cut:", f);
        pageBuilderStore.removeAddedField(blockId!, f);
      });

      // 2) Update this block’s local bars
      setLocallyUsedFields(prev => prev.filter(x => !removedFields.includes(x.field)));
      setAvailableFields(prev => {
        // reinsert back into available at the *front* (simple), or
        // reinsert at original index if you prefer:
        // use the previous locallyUsedFields snapshot to re-place by idx
        const updated = [...prev];
        // simple append (predictable):
        removedFields.forEach(f => { if (!updated.includes(f)) updated.push(f); });
        return updated;
      });
    }, 0);
  };

  /**
   * To ensure that what has been pasted into the HeadingBlock will be stripped of it's styles.
   */
  const handlePasteHTML: React.ClipboardEventHandler<HTMLElement> = (e) => {
    e.preventDefault();
    if (isCaretInsideCustomField()) moveCaretOutsideCustomField();

    const blockId = blocksManager.getBlockEditing().selectedBlockId!;
    const allAdded = pageBuilderStore.getAllAddedFields;
    const availBefore = [...availableFields];             // capture indices
    const idxMap: Record<string, number> = {};
    availBefore.forEach((f, i) => (idxMap[f] = i));

    const html = e.clipboardData.getData("text/html");
    const text = e.clipboardData.getData("text/plain");

    let result: { html: string; fields: string[] };
    if (html && html.includes('data-custom-field="true"')) {
      result = sanitizeIncomingHTMLPreservingCustomFields(html);
    } else {
      result = buildHTMLFromPlainTextWithCF(text ?? "");
    }

    // Insert sanitized HTML
    document.execCommand("insertHTML", false, result.html);

    // Dedupe vs global added list
    const uniqueNew = result.fields.filter(f => !allAdded.includes(f));
    if (uniqueNew.length) {
      pageBuilderStore.updateAddedFields(blockId, [...allAdded, ...uniqueNew]);
      // remove from available + add to locally used with original index (if known)
      setAvailableFields(prev => prev.filter(f => !uniqueNew.includes(f)));
      setLocallyUsedFields(prev => [
        ...prev,
        ...uniqueNew.map(field => ({ idx: idxMap[field] ?? -1, field }))
      ]);
    }

    const sel = window.getSelection();
    if (sel?.rangeCount) glideBarManager.setCurrCaretPos(sel.getRangeAt(0).cloneRange());
  };

  let blockClassName = "outline-none transition-all w-full";

	const isEmpty = !html || html.trim() === "" || html.trim() === "<p>&nbsp;</p>";

  console.log("isEditing: ", isEditing);

  return (
    <>
      <div
        ref={contentBlockRef}
        className={`text-gray-700 min-h-[28px] p-2 outline-none border-none tb-content-area ${className}`}
        contentEditable
        suppressContentEditableWarning={true}
        spellCheck={true}
        onClick={(e) => e.stopPropagation()}
        tabIndex={0}
        onCopy={handleCopy}
        onCut={handleCut}
        onPaste={handlePasteHTML}
        onInput={handleInput}
        onFocus={handleFocus}
        onBlur={onBlur}
        onMouseUp={(e) => {
          if (isCaretInsideCustomField()) {
            moveCaretOutsideCustomField();
          }
        }}
        onKeyDown={(e) => {
          // For detect what the user presses before the DOM changes
          if ((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'C')) {
            if (selectionIntersectsCustomField()) {
              e.preventDefault();
              e.stopPropagation();
              pageBuilderStore.setToast("Copying of custom fields is not allowed.");
              return;
            }
          }

          const selection = document.getSelection();
          const anchorNode = selection?.anchorNode;

          // 👇 Check if inside a list item
          const li =
            anchorNode?.nodeType === 3 // text node
              ? anchorNode.parentElement?.closest("li")
              : (anchorNode as HTMLElement)?.closest?.("li");

          const isInsideListItem = !!li;
          const isEmptyListItem =
            isInsideListItem && li?.textContent?.trim() === "";

          if (e.key === "Enter" && isInsideListItem) {
            if (isEmptyListItem) {
              e.preventDefault();
              // 👇 Exit the list and start a new paragraph
              document.execCommand("outdent");
              document.execCommand("insertHTML", false, "<p><br/></p>"); // or insertHTML("<p><br></p>")
            }
            return; // Let browser handle non-empty li normally
          }

          // Default Enter behavior outside of lists
          if (e.key === "Enter") {
            e.preventDefault();
            document.execCommand("insertHTML", false, "<p><br/></p>");
          }
        }}
        style={style}
        dangerouslySetInnerHTML={{
          __html: isEmpty
      			? `<p className='placeholder'>Enter your content here.</p>`
      			: html,
        }}
      ></div>
      {/* Custom Fields Bar (render at bottom) */}
      {pageBuilderStore.getIsEmail() && isEditing && availableFields.length > 0 && (
        <div className="w-full p-3 z-20 mt-4 flex gap-2 bg-transparent bg-opacity-80 rounded-b-xl border-t border-gray-200">
          {availableFields.map(field => (
            <button
              key={field}
              className={"px-3 py-1 bg-gradient-to-b from-[#f8fafc] to-[#e5e7eb] border border-gray-300 text-gray-700 rounded-md cursor-pointer text-xs font-medium hover:border-blue-400 hover:text-blue-500 transition"}
              tabIndex={-1}
              type="button"
              contentEditable={false}
              onMouseDown={e => {
                e.preventDefault();
                // Insert at caret
                if (blockId) {
                  insertCustomFieldAtCaret(field, contentBlockRef!);

                  const idx = availableFields.indexOf(field);
                  setLocallyUsedFields(prev => [...prev, {idx, field}]);
                  
                  // Update MobX store with new field
                  pageBuilderStore.updateAddedFields(blockId, [...pageBuilderStore.getAddedFields(blockId), field]);

                  console.log(JSON.stringify(pageBuilderStore.getAllAddedFields));
                  setAvailableFields(prev => prev.filter(f => f !== field));
                }
              }}
            >
              {field}
            </button>
          ))}
        </div>
      )}
    </>
  );
});

/**
 * Props for {@link ImageBlock} (UI Component).
 *
 * @remarks
 * - Extends {@link IImageBlockData}, which contains all serializable data fields (e.g.
 * 	 `src`, `captions`, `description`).
 * - All UI-only props below are for React rendering/editing only and should never be serialized.
 * - This separation helps maintain data integrity across serialization and UI rendering.
 *
 * @see {@link IImageBlockData}
 *
 * @property contentBlockRef  (Optional) Ref to the image element (for selection, toolbars, etc).
 * @property className        (Optional) Custom CSS classes for styling.
 */
export interface IImageBlockProps extends IImageBlockData {
  contentBlockRef?: RefObject<HTMLImageElement>;
  className?: string;
  /** Called when the block is clicked (to trigger editing/selecting). */
  onClick?: (e: MouseEvent<HTMLImageElement>) => void;
  onImageUrlChange?: (url: string) => void;
}

/**
 * ImageBlock – Renders an image content block with optional caption and description.
 *
 * @remarks
 * - The image is displayed full width by default.
 * - Use the `caption` prop to display a short label or credit below the image.
 * - The `description` prop maps to the image's alt text for accessibility (screen readers, SEO).
 * - Future enhancements can support uploading, cropping, or caption formatting.
 *
 * @example
 * <ImageBlock
 *   src="https://example.com/image.jpg"
 *   caption="My vacation"
 *   description="A photo of a beach at sunset"
 *   contentBlockRef={ref}
 *   className="rounded-lg"
 * />
 */
export function ImageBlock({
  src,
  caption,
  alt,
  description = "",
  contentBlockRef,
  className = "",
  margin,
  borderRadius,
  width,
  height,
  alignment,
  imageFit,
  imagePosition,
  onClick,
  onImageUrlChange,
}: IImageBlockProps) {
  const [{ isOver }, dropRef] = useDrop({
    accept: "ASSET_IMAGE",
    drop: (item: { url: string }) => {
      onImageUrlChange!(item.url);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  const imgStyle: React.CSSProperties = {
    width: width?.value ? `${width.value}${width.unit || "%"}` : "100%",
    height: height?.value ? `${height.value}${height.unit || "px"}` : "auto",
    borderRadius: blocksManager.dimensionToCss(borderRadius),
    objectFit: imageFit || "cover",
    objectPosition: imagePosition || "center",
    display: "block",
  };

  return (
    <figure
      className={`max-w-full border border-dashed border-gray-300 ${className}`}
      ref={dropRef}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems:
          alignment === "center"
            ? "center"
            : alignment === "right"
            ? "flex-end"
            : "flex-start",
        padding: blocksManager.dimensionToCss(margin),
      }}
    >
      <img
        ref={contentBlockRef}
        src={src}
        alt={alt}
        aria-description={description}
        className="w-full h-auto"
        draggable={false}
        onClick={onClick}
        style={imgStyle}
      />
      {caption && (
        <figcaption className=" text-gray-500 text-sm mt-1"
          style={{
            textAlign: alignment
          }}
        >
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

/**
 * Props for {@link HeroSliderBlock}.
 *
 * @remarks
 * - Extends {@link IHeroSliderBlockData}, which contains all serializable data fields (e.g. `images`, etc).
 * - All UI-only props below are for React rendering/editing only and should never be serialized.
 * - This pattern keeps your codebase type-safe and serialization-ready.
 *
 * @see {@link IHeroSliderBlockData}
 *
 * @property contentBlockRef  (Optional) Ref to the carousel container.
 * @property className        (Optional) Custom CSS classes for styling.
 */
export interface IHeroSliderBlockProps {
  slides: IHeroSlide[];
  showArrows?: boolean;
  showIndicators?: boolean;
  width?: IDimensionField;
  height?: IDimensionField;
  borderRadius?: { value: string; unit: string };
  backgroundColor?: string;
  contentBlockRef?: RefObject<HTMLDivElement>;
  className?: string;
  onClick?: (e: MouseEvent<HTMLImageElement>) => void;
  selectedIndex: number;
}

export function HeroSliderBlock({
  selectedIndex,
  slides,
  showArrows = true,
  showIndicators = true,
  width,
  height,
  borderRadius,
  backgroundColor,
  contentBlockRef,
  className = "",
  onClick,
}: IHeroSliderBlockProps) {
  const [current, setCurrent] = useState<number>(selectedIndex);

  const [{ isOver }, dropRef] = useDrop({
    accept: "ASSET_IMAGE",
    drop: (item: { url: string }) => {
      if (slides && slides.length > 0 && typeof selectedIndex === "number") {
        // Update only the dropped slide's image
        const updatedSlides = slides.map((slide, idx) =>
          idx === selectedIndex
            ? {
                ...slide,
                slideImage: { ...slide.slideImage, src: item.url },
              }
            : slide
        );

        // Update MobX images array (customize to your state logic)
        contentManager.setHeroSliderConfig(
          blocksManager.getBlockEditing().selectedBlockId!,
          { slides: updatedSlides }
        );

        // Update block editing state (optional, based on your app)
        blocksManager.setHeroSlideEditing({
          selectedSlideIndex: selectedIndex,
          selectedSlide: updatedSlides[selectedIndex],
        });
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  if (!slides || slides.length === 0) {
    return (
      <div
        className={`border border-dashed border-gray-300 p-4 bg-gray-100 text-gray-400 text-center ${className}`}
        style={{
          width: width ? `${width.value}${width.unit}` : undefined,
          height: height ? `${height.value}${height.unit}` : undefined,
          borderRadius: borderRadius
            ? `${borderRadius.value}${borderRadius.unit}`
            : undefined,
          background: backgroundColor || "#f8fafc",
        }}
        onClick={onClick}
      >
        No images in carousel.
      </div>
    );
  }

  const slide = slides[selectedIndex] || slides[0];

  // Helper to switch slides, looping if needed
  const goTo = (idx: number) =>
    setCurrent(((idx % slides.length) + slides.length) % slides.length);

  return (
    <div ref={dropRef}>
      <div
        ref={contentBlockRef}
        className={`relative border border-dashed border-gray-300 p-4 ${className}`}
        style={{
          width: width ? `${width.value}${width.unit}` : undefined,
          height: height ? `${height.value}${height.unit}` : undefined,
          borderRadius: borderRadius
            ? `${borderRadius.value}${borderRadius.unit}`
            : undefined,
          background: backgroundColor || "#f8fafc",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
        onClick={onClick}
      >
        <div
          className="relative w-full h-full flex-1 overflow-hidden rounded-lg"
          style={{
            border: isOver ? "2px solid #60a5fa" : undefined,
            height: "100%",
          }}
        >
          {/* Background Image */}
          <img
            src={slide.slideImage?.src}
            alt={slide.slideImage?.alt || "..."}
            className="absolute inset-0 w-full h-full object-cover z-0 pointer-events-none"
            style={{
              borderRadius: borderRadius
                ? `${borderRadius.value}${borderRadius.unit}`
                : undefined,
              objectFit: slide.slideImage?.imageFit || "cover",
              objectPosition: slide.slideImage?.imagePosition || "center",
            }}
            draggable={false}
          />

          {/* Overlay */}
          {slide.overlayColor && (
            <div
              style={{
                background: slide.overlayColor,
                position: "absolute",
                zIndex: 1,
                inset: 0,
                pointerEvents: "none",
                borderRadius: borderRadius
                  ? `${borderRadius.value}${borderRadius.unit}`
                  : undefined,
              }}
            />
          )}

          {/* SLIDE CONTENT */}
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 z-20">
            {/* --- HERO MODE --- */}
            {slide.isHeroMode ? (
              <>
                {/* Hero Header */}
                {slide.heroHeader?.text && (
                  <h3
                    style={{
                      color: slide.heroHeader.color ?? "#fff",
                      fontFamily:
                        slide.heroHeader.fontFamily ?? "Montserrat, sans-serif",
                      fontSize: slide.heroHeader.fontSize?.value
                        ? `${slide.heroHeader.fontSize.value}${slide.heroHeader.fontSize.unit || "rem"}`
                        : "1.875rem",
                      lineHeight: slide.heroHeader.lineHeight?.value
                        ? `${slide.heroHeader.lineHeight.value}${slide.heroHeader.lineHeight.unit || ""}`
                        : "1.2",
                      letterSpacing: slide.heroHeader.letterSpacing?.value
                        ? `${slide.heroHeader.letterSpacing.value}${slide.heroHeader.letterSpacing.unit || "px"}`
                        : "0px",
                      margin: blocksManager.dimensionToCss(
                        slide.heroHeader.margin
                      ),
                      fontWeight: slide.heroHeader.fontWeight ?? "700",
                      padding: blocksManager.dimensionToCss(
                        slide.heroHeader.padding
                      ),
                      textShadow: "0 2px 12px rgba(0,0,0,0.24)",
                      textDecoration: slide.heroHeader.textDecoration || "none",
                      textTransform: (slide.heroHeader.textTransform ||
                        "none") as TextTransformType,
                    }}
                    className="text-center mt-6"
                  >
                    {slide.heroHeader.text}
                  </h3>
                )}

                {/* Hero Text */}
                {slide.heroText?.text && (
                  <p
                    style={{
                      color: slide.heroText.color ?? "#fff",
                      fontFamily:
                        slide.heroText.fontFamily ?? "Inter, sans-serif",
                      fontSize: slide.heroText.fontSize?.value
                        ? `${slide.heroText.fontSize.value}${slide.heroText.fontSize.unit || "rem"}`
                        : "1rem",
                      lineHeight: slide.heroText.lineHeight?.value
                        ? `${slide.heroText.lineHeight.value}${slide.heroText.lineHeight.unit || ""}`
                        : "1.5rem",
                      letterSpacing: slide.heroText.letterSpacing?.value
                        ? `${slide.heroText.letterSpacing.value}${slide.heroText.letterSpacing.unit || "px"}`
                        : "0px",
                      margin: blocksManager.dimensionToCss(
                        slide.heroText.margin
                      ),
                      fontWeight: slide.heroText.fontWeight ?? "400",
                      padding: blocksManager.dimensionToCss(
                        slide.heroText.padding
                      ),
                      textShadow: "0 1px 10px rgba(0,0,0,0.13)",
                      textDecoration: slide.heroText.textDecoration || "none",
                      textTransform: (slide.heroText.textTransform ||
                        "none") as TextTransformType,
                    }}
                    className="text-center"
                  >
                    {slide.heroText.text}
                  </p>
                )}

                {/* CTA Button */}
                {slide.cta?.text && (
                  <div
                    className={`flex mt-4 ${
                      slide.cta.alignment === "left"
                        ? "justify-start"
                        : slide.cta.alignment === "right"
                          ? "justify-end"
                          : "justify-center"
                    }`}
                  >
                    <a
                      href={slide.cta.url ?? "#"}
                      target={slide.cta.openInNewTab ? "_blank" : "_self"}
                      rel="noopener noreferrer"
                      className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 font-semibold transition"
                      style={{
                        textAlign: slide.cta.alignment ?? "center",
                      }}
                    >
                      {slide.cta.text}
                    </a>
                  </div>
                )}
              </>
            ) : (
              // --- CAROUSEL MODE ---
              <>
                {/* (Optional) Carousel Caption/Description */}
                {slide.slideImage?.caption && (
                  <div
                    className="absolute bottom-4 left-0 right-0 px-4 z-20 bg-black/40 rounded text-white text-base font-semibold"
                    style={{
                      pointerEvents: "none",
                      margin: "0 auto",
                      maxWidth: "80%",
                    }}
                  >
                    {slide.slideImage.caption}
                  </div>
                )}
              </>
            )}

            {/* Indicators */}
            {showIndicators && (
              <div className="flex justify-center items-center gap-2 mt-4">
                {slides.map((_, idx) => (
                  <span
                    key={idx}
                    onClick={() => setCurrent(idx)}
                    className={`w-2 h-2 rounded-full mx-1 transition-all duration-200 cursor-pointer ${
                      idx === selectedIndex
                        ? "bg-blue-500 scale-110 shadow"
                        : "bg-gray-300"
                    }`}
                    style={{ display: "inline-block" }}
                  />
                ))}
              </div>
            )}

            {/* Arrows */}
            {showArrows && (
              <>
                {/* Left Arrow */}
                <button
                  type="button"
                  aria-label="Previous slide"
                  onClick={(e) => {
                    e.stopPropagation();
                    goTo(selectedIndex - 1);
                  }}
                  className="
										absolute left-3 top-1/2 -translate-y-1/2 z-30
										flex items-center justify-center
										w-12 h-12
										rounded-full
										bg-black/10
										hover:bg-black/30
										text-white
										transition
										opacity-70
										hover:opacity-95
										focus:outline-none
										select-none
										shadow-lg
									"
                  style={{
                    border: "none",
                    boxShadow: "0 2px 16px rgba(0,0,0,0.08)",
                    cursor: "pointer",
                  }}
                >
                  <ChevronLeft size={32} strokeWidth={3} />
                </button>

                {/* Right Arrow */}
                <button
                  type="button"
                  aria-label="Next slide"
                  onClick={(e) => {
                    e.stopPropagation();
                    goTo(selectedIndex + 1);
                  }}
                  className="
										absolute right-3 top-1/2 -translate-y-1/2 z-30
										flex items-center justify-center
										w-12 h-12
										rounded-full
										bg-black/10
										hover:bg-black/30
										text-white
										transition
										opacity-70
										hover:opacity-95
										focus:outline-none
										select-none
										shadow-lg
									"
                  style={{
                    border: "none",
                    boxShadow: "0 2px 16px rgba(0,0,0,0.08)",
                    cursor: "pointer",
                  }}
                >
                  <ChevronRight size={32} strokeWidth={3} />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Props for {@link VideoBlock} (UI Component).
 *
 * @remarks
 * - Extends {@link IVideoBlockData}, which contains all serializable data fields (e.g. `url`).
 * - All UI-only props below are for React rendering/editing only and should never be serialized.
 * - This separation supports maintainability and safe (de)serialization.
 *
 * @see {@link IVideoBlockData}
 *
 * @property contentBlockRef  (Optional) Ref to the video container (for selection, toolbars, etc).
 * @property className        (Optional) Custom CSS classes for styling.
 */
export interface IVideoBlockProps extends IVideoBlockData {
  contentBlockRef?: RefObject<HTMLDivElement>;
  className?: string;
  onClick?: (e: MouseEvent<HTMLDivElement>) => void;
}

/**
 * VideoBlock – Renders an embedded video block (YouTube or similar).
 *
 * @remarks
 * By default, this displays a responsive video embed using an iframe (for YouTube URLs).
 * Future enhancements can support captions, custom thumbnails, or direct video file links.
 *
 * @example
 * <VideoBlock
 *   url="https://www.youtube.com/embed/xyz123"
 *   contentBlockRef={ref}
 *   className="rounded-lg"
 * />
 */
export const VideoBlock = observer(function VideoBlock({
  url,
  contentBlockRef,
  className = "",
  onClick,
  padding,
}: IVideoBlockProps) {
  const isPreview = pageBuilderStore.previewMode.showPreview;
  // For now, expect a valid embed URL (e.g., https://www.youtube.com/embed/xyz)
  // Future: add URL parsing to support different video providers
  console.log("padding:", JSON.stringify(padding));
  return (
    <div
      ref={contentBlockRef}
      className={`border border-dashed border-gray-300 flex items-center justify-center aspect-video ${className}`}
      onClick={onClick}
      style={{
        cursor: isPreview ? "auto" : "pointer",
        position: "relative",
      }}
    >
      {url ? (
        isPreview ? (
          // Show the actual video iframe only in preview mode
          <iframe
            src={url}
            title="Embedded Video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full rounded border-none"
            style={{
              margin: padding ? `${padding.value}${padding.unit}` : '0px',
            }}
          />
        ) : (
          // In Design mode: render a static placeholder
          <div
            className="flex flex-col items-center justify-center w-full h-full bg-black text-white opacity-70 select-none pointer-events-none"
            style={{
              margin: padding ? `${padding.value}${padding.unit}` : '0px',
            }}
          >
            <PlayCircle size={56} className="mb-2 opacity-90" />
            <span className="text-xs text-gray-200 font-mono break-all">
              {url}
            </span>
            <span className="text-xs text-gray-400">
              [Video disabled in Design mode]
            </span>
          </div>
        )
      ) : (
        <span className="text-white text-center">No video URL set</span>
      )}
    </div>
  );
});

/**
 * Props for {@link SocialsBlock} (UI Component).
 *
 * @remarks
 * - Extends {@link ISocialsBlockData}, which contains all serializable data fields (e.g. `socials`, etc).
 * - All UI-only props below are for React rendering/editing only and should never be serialized.
 * - This pattern makes our UI and data logic clearly separated and easy to maintain.
 *
 * @property contentBlockRef	(Optional) Ref to the socials container.
 * @property className	      (Optional) Custom CSS classes.
 */
export interface ISocialsBlockProps extends ISocialsBlockData {
  contentBlockRef?: RefObject<HTMLDivElement>;
  className?: string;
  onClick?: (e: MouseEvent<HTMLDivElement>) => void;
}

/**
 * SocialsBlock – Renders a row of social media icons as links.
 *
 * @remarks
 * Each icon is rendered from lucide-react. No colors by default.
 * Future enhancements: allow color selection, reorder, add more platforms, custom tooltips, etc.
 *
 * @example
 * <SocialsBlock
 *   socials={[
 *     { type: "facebook", url: "https://facebook.com/example" },
 *     { type: "twitter", url: "https://twitter.com/example" }
 *   ]}
 *   contentBlockRef={ref}
 * />
 */
export function SocialsBlock({
  socials,
  iconShape = "circle", // Add these for explicitness and fallback
  iconSize = 32,
  alignment = "left",
  spacing = 8,
  contentBlockRef,
  className = "",
  onClick,
}: ISocialsBlockProps) {
  // 2. Map SocialType to icon and brand color
  const SOCIALS_ICON_META: Record<string, { Icon: any; color: string }> = {
    facebook: { Icon: SiFacebook, color: "#4267B2" },
    twitter: { Icon: SiX, color: "#1DA1F2" },
    instagram: { Icon: SiInstagram, color: "#E1306C" },
    linkedin: { Icon: SiLinkedin, color: "#2867B2" },
    youtube: { Icon: SiYoutube, color: "#FF0000" },
    tiktok: { Icon: SiTiktok, color: "#000000" },
    whatsapp: { Icon: SiWhatsapp, color: "#25D366" },
    telegram: { Icon: SiTelegram, color: "#0088cc" },
    github: { Icon: SiGithub, color: "#333" },
  };

  return (
    <div
      ref={contentBlockRef}
      className={`flex items-center border border-dashed border-gray-300 p-2 ${className}`}
      style={{
        gap: `${spacing}px`,
        justifyContent:
          alignment === "left"
            ? "flex-start"
            : alignment === "right"
              ? "flex-end"
              : "center",
      }}
      onClick={onClick}
    >
      {socials && socials.length > 0 ? (
        socials.map((item, idx) => {
          const meta = SOCIALS_ICON_META[item.type];
          const Icon = meta?.Icon;
          const iconColor = item.color || meta?.color || "#888888";
          return (
            <a
              key={item.type + idx}
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center justify-center transition p-2
                ${iconShape === "circle" ? "rounded-full" : "rounded"}
                hover:shadow-md`}
              aria-label={item.type}
              style={{
                background: "#ffffff",
                width: iconSize,
                height: iconSize,
                minWidth: iconSize,
                minHeight: iconSize,
                boxShadow: "0 1px 4px rgba(30, 60, 90, 0.07)",
                border: "1.5px solid #eeeeee",
              }}
            >
              {Icon ? (
                <Icon size={iconSize * 0.6} color={iconColor} />
              ) : (
                <span className="w-6 h-6 flex items-center justify-center text-gray-400">
                  ?
                </span>
              )}
            </a>
          );
        })
      ) : (
        <span className="text-gray-400 text-sm">No socials configured</span>
      )}
    </div>
  );
}

/**
 * Props for {@link HtmlBlock} (UI Component).
 *
 * @remarks
 * - Extends {@link IHtmlBlockData}, which contains all serializable data fields (e.g. `html`, etc).
 * - All UI-only props below are for React rendering/editing only and should never be serialized.
 * - This ensures HTML block data is always kept clean and UI logic is easy to maintain.
 *
 * @see {@link IHtmlBlockData}
 *
 * @property contentBlockRef	(Optional) Ref to the container element.
 * @property className	      (Optional) Custom CSS classes for styling.
 */
export interface IHtmlBlockProps extends IHtmlBlockData {
  contentBlockRef?: RefObject<HTMLDivElement>;
  className?: string;
  onClick?: (e: MouseEvent<HTMLDivElement>) => void;
}

/**
 * HtmlBlock – Renders custom HTML content as provided by the user.
 *
 * @remarks
 * Use this block to allow users to inject raw HTML snippets into their page or email.
 * This can be powerful but may have limitations (e.g., certain tags may be sanitized or disabled in email mode).
 *
 * ⚠️ Always validate/sanitize user HTML before saving or rendering in production to prevent XSS.
 *
 * @example
 * <HtmlBlock
 *   html={`<p style="color: red;">Hello <b>world</b>!</p>`}
 *   contentBlockRef={ref}
 *   className="bg-gray-50"
 * />
 */
export function HtmlBlock({
  html,
  contentBlockRef,
  className = "",
  onClick,
  padding,
}: IHtmlBlockProps) {
	const hasIframe = /<iframe[\s>]/i.test(html);
	const hasScript = /<script[\s>]/i.test(html);
	
  return (
    <div
      ref={contentBlockRef}
      className={`border border-dashed border-gray-300 flex items-center justify-center ${className}`}
      onClick={onClick}
      style={{
        padding: `${padding.value}${padding.unit}`,
      }}
    >
      {(hasIframe || hasScript) ? (
        <div
          className="flex flex-col items-center justify-center w-full aspect-video bg-black text-white opacity-70"
        >
          <span className="text-xs text-gray-400">
            [Embedded Videos are disabled in Design mode]
          </span>
        </div>
      ) : (
        <div
          className="w-full h-auto"
          // Rendering raw HTML: be sure HTML is sanitized upstream!
          dangerouslySetInnerHTML={{ __html: html }}
        />
      )}
    </div>
  );
}
